import { decode } from 'blurhash';
import { MediaPosterImage } from 'media-chrome';

const ATTR_BLURHASH = 'blurhash' as const;
const ATTR_BLURHASH_WIDTH = 'blurhash-width' as const;
const ATTR_BLURHASH_HEIGHT = 'blurhash-height' as const;
const ATTR_VIDEO_WIDTH = 'video-width' as const;
const ATTR_VIDEO_HEIGHT = 'video-height' as const;
const TRANSPARENT_PIXEL =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';

interface CanvasDimensions {
  width: number;
  height: number;
}

/**
 * Custom poster image component that extends Media Chrome's poster image with blurhash support
 *
 * @attr {string} blurhash - BlurHash string for placeholder image generation
 * @attr {string} blurhash-width - Width for blurhash canvas rendering
 * @attr {string} blurhash-height - Height for blurhash canvas rendering
 * @attr {string} video-width - Original video width for auto-calculating canvas size
 * @attr {string} video-height - Original video height for auto-calculating canvas size
 * @attr {string} src - Poster image source URL (inherited)
 * @attr {string} placeholdersrc - Placeholder image source URL (inherited)
 */
export class BunnyPosterImage extends MediaPosterImage {
  private blurhashCanvas: HTMLCanvasElement | null = null;
  private controllerObserver: MutationObserver | null = null;
  private imageLoaded: boolean = false;
  private initialized: boolean = false;

  // Add proper type for inherited image property
  declare image: HTMLImageElement;

  static override get observedAttributes(): string[] {
    return [
      ...(MediaPosterImage.observedAttributes || []),
      ATTR_BLURHASH,
      ATTR_BLURHASH_WIDTH,
      ATTR_BLURHASH_HEIGHT,
      ATTR_VIDEO_WIDTH,
      ATTR_VIDEO_HEIGHT,
    ];
  }

  constructor() {
    super();
  }

  private getMediaController(): HTMLElement | null {
    if (typeof this.closest !== 'function') return null;
    return this.closest('media-controller');
  }

  private connectControllerObserver(): void {
    this.disconnectControllerObserver();
    const controller = this.getMediaController();
    if (!controller) return;

    this.controllerObserver = new MutationObserver(() => {
      this.syncVisibility();
    });

    this.controllerObserver.observe(controller, {
      attributes: true,
      attributeFilter: ['data-trailer-active'],
    });
  }

  private disconnectControllerObserver(): void {
    if (this.controllerObserver) {
      this.controllerObserver.disconnect();
      this.controllerObserver = null;
    }
  }

  private hasValidSrcAttribute(): boolean {
    const src = this.getAttribute('src');
    if (!src) return false;
    const trimmed = src.trim();
    if (!trimmed) return false;
    try {
      new URL(trimmed, globalThis.location?.href);
      return true;
    } catch {
      return false;
    }
  }

  private isTrailerActive(): boolean {
    const controller = this.getMediaController();
    return controller?.dataset?.trailerActive === 'true';
  }

  private syncVisibility(): void {
    if (!this.initialized) return;

    if (this.isTrailerActive()) {
      this.style.display = 'none';
      return;
    }

    if (!this.hasValidSrcAttribute() && !this.getAttribute(ATTR_BLURHASH)) {
      this.style.display = 'none';
      return;
    }

    this.style.display = '';
  }

  private initialize(): void {
    this.initialized = true;

    // Get the image element from shadow DOM
    if (this.shadowRoot) {
      const imageElement = this.shadowRoot.querySelector('#image') ||
        this.shadowRoot.querySelector('img');
      if (imageElement instanceof HTMLImageElement) {
        this.image = imageElement;
      }
    }

    if (!this.image) {
      console.warn(
        'BunnyPosterImage: Could not find image element in shadow DOM',
      );
      return;
    }

    // Set up image load event listeners
    this.image.addEventListener('load', () => {
      this.imageLoaded = true;
      this.hideBlurhash();
      this.syncVisibility();
    });

    this.image.addEventListener('error', () => {
      this.imageLoaded = false;
      if (this.getAttribute(ATTR_BLURHASH)) {
        this.showBlurhash();
        this.suppressBrokenImageIcon();
        this.syncVisibility();
        return;
      }
      // No fallback available, hide poster image instead of showing broken icon.
      this.style.display = 'none';
    });

    // Apply blurhash if attribute is already set
    const blurhash = this.getAttribute(ATTR_BLURHASH);
    if (blurhash) {
      this.renderBlurhash();
    }

    this.connectControllerObserver();
    this.syncVisibility();
  }

  override attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (oldValue === newValue) return;

    if (name === ATTR_BLURHASH) {
      if (newValue && this.initialized) {
        this.renderBlurhash();
      } else if (!newValue) {
        this.hideBlurhash();
      }
    }

    // Re-render blurhash if dimensions change
    if (
      name === ATTR_BLURHASH_WIDTH || name === ATTR_BLURHASH_HEIGHT ||
      name === ATTR_VIDEO_WIDTH || name === ATTR_VIDEO_HEIGHT
    ) {
      if (this.initialized && this.getAttribute(ATTR_BLURHASH)) {
        this.renderBlurhash();
      }
    }

    // Reset image loaded state when src changes
    if (name === 'src') {
      this.imageLoaded = false;
      if (newValue && this.getAttribute(ATTR_BLURHASH)) {
        // Show blurhash again when new image starts loading
        this.showBlurhash();
      }
      this.syncVisibility();
    }
  }

  connectedCallback(): void {
    // Try synchronous initialization first
    if (!this.initialized) {
      if (this.shadowRoot && this.shadowRoot.querySelector('#image')) {
        this.initialize();
      } else {
        // Fallback to async if shadow DOM not ready
        requestAnimationFrame(() => {
          if (!this.initialized) {
            this.initialize();
          }
        });
      }
    }

    // Render blurhash if needed after initialization
    if (this.initialized) {
      this.renderBlurhashIfNeeded();
    }
  }

  disconnectedCallback(): void {
    this.disconnectControllerObserver();
    this.cleanup();
  }

  private renderBlurhashIfNeeded(): void {
    const blurhash = this.getAttribute(ATTR_BLURHASH);
    if (blurhash && !this.imageLoaded) {
      this.renderBlurhash();
    }
  }

  private getBlurhashDimensions(): CanvasDimensions {
    // Priority 1: Check for explicit blurhash canvas dimensions
    const blurhashWidthAttr = this.getAttribute(ATTR_BLURHASH_WIDTH);
    const blurhashHeightAttr = this.getAttribute(ATTR_BLURHASH_HEIGHT);

    let width = blurhashWidthAttr ? parseInt(blurhashWidthAttr, 10) : null;
    let height = blurhashHeightAttr ? parseInt(blurhashHeightAttr, 10) : null;

    // Validate explicit blurhash dimensions
    if (width && (width < 4 || width > 128)) width = null;
    if (height && (height < 4 || height > 128)) height = null;

    // If explicit dimensions provided, use them
    if (width && height) {
      return { width, height };
    }

    // Priority 2: Auto-calculate from video dimensions
    const videoWidthAttr = this.getAttribute(ATTR_VIDEO_WIDTH);
    const videoHeightAttr = this.getAttribute(ATTR_VIDEO_HEIGHT);

    let videoWidth = videoWidthAttr ? parseInt(videoWidthAttr, 10) : null;
    let videoHeight = videoHeightAttr ? parseInt(videoHeightAttr, 10) : null;

    // Validate video dimensions (support up to 8K)
    if (videoWidth && (videoWidth < 1 || videoWidth > 8192)) videoWidth = null;
    if (videoHeight && (videoHeight < 1 || videoHeight > 8192)) {
      videoHeight = null;
    }

    if (videoWidth && videoHeight) {
      return this.calculateOptimalCanvasSize(videoWidth, videoHeight);
    }

    // Priority 3: Handle partial dimensions
    if (width && !height) {
      if (videoWidth && videoHeight) {
        // Use video aspect ratio
        const aspectRatio = videoHeight / videoWidth;
        height = Math.round(width * aspectRatio);
      } else {
        // Fallback to 16:9
        height = Math.round((width * 9) / 16);
      }
      return {
        width: Math.max(4, Math.min(128, width)),
        height: Math.max(4, Math.min(128, height)),
      };
    }

    if (height && !width) {
      if (videoWidth && videoHeight) {
        // Use video aspect ratio
        const aspectRatio = videoWidth / videoHeight;
        width = Math.round(height * aspectRatio);
      } else {
        // Fallback to 16:9
        width = Math.round((height * 16) / 9);
      }
      return {
        width: Math.max(4, Math.min(128, width)),
        height: Math.max(4, Math.min(128, height)),
      };
    }

    // Priority 4: Default fallback
    return { width: 48, height: 27 }; // 16:9 ratio, optimal for most blurhash
  }

  private calculateOptimalCanvasSize(
    videoWidth: number,
    videoHeight: number,
  ): CanvasDimensions {
    // Use the longer dimension to determine quality tier (handles rotation)
    const longerDimension = Math.max(videoWidth, videoHeight);
    const aspectRatio = videoHeight / videoWidth;

    // Target canvas size based on the longer dimension of the video
    let targetCanvasSize: number;
    if (longerDimension <= 720) {
      targetCanvasSize = 32; // SD content
    } else if (longerDimension <= 1920) {
      targetCanvasSize = 48; // HD content
    } else {
      targetCanvasSize = 64; // 4K+ content (optimal for blurhash)
    }

    // Calculate canvas dimensions maintaining aspect ratio
    let canvasWidth: number;
    let canvasHeight: number;
    if (videoWidth >= videoHeight) {
      // Landscape or square - use targetCanvasSize as width
      canvasWidth = targetCanvasSize;
      canvasHeight = Math.round(targetCanvasSize * aspectRatio);
    } else {
      // Portrait - use targetCanvasSize as height
      canvasHeight = targetCanvasSize;
      canvasWidth = Math.round(targetCanvasSize / aspectRatio);
    }

    // Ensure reasonable bounds for blurhash rendering
    return {
      width: Math.max(16, Math.min(128, canvasWidth)),
      height: Math.max(9, Math.min(128, canvasHeight)),
    };
  }

  private renderBlurhash(): void {
    const blurhashString = this.getAttribute(ATTR_BLURHASH);
    if (!blurhashString || !this.image) {
      return;
    }

    try {
      // Get dynamic dimensions
      const { width, height } = this.getBlurhashDimensions();

      // Create canvas for blurhash
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('BunnyPosterImage: Failed to get 2D context from canvas');
        return;
      }

      // Decode blurhash to pixel data
      const pixels = decode(blurhashString, width, height);

      // Create ImageData and set pixels
      const imageData = ctx.createImageData(width, height);
      imageData.data.set(pixels);
      ctx.putImageData(imageData, 0, 0);

      // Convert canvas to data URL
      const dataUrl = canvas.toDataURL('image/png');

      // Store canvas reference for cleanup
      this.blurhashCanvas = canvas;

      // Apply blurhash as background image with blur effect
      this.image.style.backgroundImage = `url(${dataUrl})`;
      this.image.style.backgroundSize = 'cover';
      this.image.style.backgroundPosition = 'center';
      this.image.style.backgroundRepeat = 'no-repeat';

      // Add a subtle blur effect to make it look more like a placeholder
      this.image.style.filter = 'blur(1px)';
    } catch (error) {
      console.error('BunnyPosterImage: Failed to render blurhash:', error);
    }
  }

  private showBlurhash(): void {
    if (this.image && !this.imageLoaded && this.getAttribute(ATTR_BLURHASH)) {
      this.renderBlurhash();
    }
  }

  private suppressBrokenImageIcon(): void {
    if (!this.image) return;
    // Replace failed image source with a transparent pixel to avoid the browser's broken-image icon.
    this.image.src = TRANSPARENT_PIXEL;
  }

  private hideBlurhash(): void {
    if (this.image) {
      // Clear background image and filter
      this.image.style.backgroundImage = '';
      this.image.style.filter = '';
    }
    this.cleanup();
  }

  private cleanup(): void {
    if (this.blurhashCanvas) {
      this.blurhashCanvas = null;
    }
  }

  // Public API
  get blurhash(): string | null {
    return this.getAttribute(ATTR_BLURHASH);
  }

  set blurhash(value: string | null) {
    if (value) {
      this.setAttribute(ATTR_BLURHASH, value);
    } else {
      this.removeAttribute(ATTR_BLURHASH);
    }
  }

  get blurhashWidth(): number | null {
    const value = this.getAttribute(ATTR_BLURHASH_WIDTH);
    return value ? parseInt(value, 10) : null;
  }

  set blurhashWidth(value: number | null) {
    if (value && typeof value === 'number' && value > 0) {
      this.setAttribute(ATTR_BLURHASH_WIDTH, value.toString());
    } else {
      this.removeAttribute(ATTR_BLURHASH_WIDTH);
    }
  }

  get blurhashHeight(): number | null {
    const value = this.getAttribute(ATTR_BLURHASH_HEIGHT);
    return value ? parseInt(value, 10) : null;
  }

  set blurhashHeight(value: number | null) {
    if (value && typeof value === 'number' && value > 0) {
      this.setAttribute(ATTR_BLURHASH_HEIGHT, value.toString());
    } else {
      this.removeAttribute(ATTR_BLURHASH_HEIGHT);
    }
  }

  get videoWidth(): number | null {
    const value = this.getAttribute(ATTR_VIDEO_WIDTH);
    return value ? parseInt(value, 10) : null;
  }

  set videoWidth(value: number | null) {
    if (value && typeof value === 'number' && value > 0) {
      this.setAttribute(ATTR_VIDEO_WIDTH, value.toString());
    } else {
      this.removeAttribute(ATTR_VIDEO_WIDTH);
    }
  }

  get videoHeight(): number | null {
    const value = this.getAttribute(ATTR_VIDEO_HEIGHT);
    return value ? parseInt(value, 10) : null;
  }

  set videoHeight(value: number | null) {
    if (value && typeof value === 'number' && value > 0) {
      this.setAttribute(ATTR_VIDEO_HEIGHT, value.toString());
    } else {
      this.removeAttribute(ATTR_VIDEO_HEIGHT);
    }
  }

  /**
   * Set both blurhash canvas width and height at once
   */
  setBlurhashDimensions(width?: number, height?: number): void {
    if (width) this.blurhashWidth = width;
    if (height) this.blurhashHeight = height;
  }

  /**
   * Set video dimensions for auto-calculating optimal blurhash canvas size
   */
  setVideoDimensions(width?: number, height?: number): void {
    if (width) this.videoWidth = width;
    if (height) this.videoHeight = height;
  }
}

if (!globalThis.customElements.get('bunny-poster-image')) {
  globalThis.customElements.define('bunny-poster-image', BunnyPosterImage);
}

export default BunnyPosterImage;
